import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useMobileApi } from "../../Api/Mobile/useMobileApi";
import { AuthMobileEmployeeResponse, DeleteCurrentFcmTokenRequest, EmployeeMobileRequestsVovClient, EmployeeMobileVovClient, InsertLogRecordRequest, MeEmployeeResponse, MobileApplicationsVovClient, MobileRecordsVovClient } from "../../ApiOld/Api/ApiServerVov";
import { logoutEmployee } from "../../Components/Shared/Auth/Employee/logoutEmployee";
import { enqueueSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router";

interface EmployeeLayoutProps {
    employeeInfo: MeEmployeeResponse | null
    loggedIn: boolean | null;
    login: (data: AuthMobileEmployeeResponse) => void;
    logout: () => void;
    companySubdomain: string;
    getFirstPartPath: (lang: string) => string;
    disabledModules: string[];
    moduleButton: JSX.Element | null;
    setModuleButton: (button: JSX.Element | null) => void;
    moduleName: string;
    setModuleName: (name: string) => void;
    photoTemp: string | undefined;
    setPhotoTemp: React.Dispatch<React.SetStateAction<string | undefined>>;
    menuHidden: boolean;
    setMenuHidden: (hidden: boolean) => void;
    moduleSecondButton: JSX.Element | null;
    setModuleSecondButton?: (button: JSX.Element | null) => void;
    networkType: string;
    unreadMessages: number;
    setUnreadMessages: React.Dispatch<React.SetStateAction<number>>;
    title: string;
    setTitle?: (title: string) => void;
    diagnosticAlerts: boolean;
    setDiagnosticAlerts: (value: boolean) => void;
    deviceModel: string;
    setDeviceModel: (model: string) => void;
    deviceId: string;
    setDeviceId: (id: string) => void;
    platform: string;
    setPlatform: (platform: string) => void;
    fcmToken: string;
    setFcmToken: (token: string) => void;
    versionWithBuild: string;
    setVersionWithBuild: (version: string) => void;
}
const EmployeeLayoutContext = createContext<EmployeeLayoutProps | undefined>(undefined);
export const EmployeeLayoutProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { authorizedMobileEmployeeApi: employeeApi } = useMobileApi(EmployeeMobileVovClient);
    const { authorizedMobileEmployeeApi: requestsApi } = useMobileApi(EmployeeMobileRequestsVovClient);
    const { authorizedMobileEmployeeApi: recordsApi } = useMobileApi(MobileRecordsVovClient);
    const client = useMobileApi(MobileApplicationsVovClient);
    const [employeeInfo, setEmployeeInfo] = useState<MeEmployeeResponse | null>(null);
    const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
    const [moduleButton, setModuleButton] = useState<JSX.Element | null>(null);
    const [moduleSecondButton, setModuleSecondButton] = useState<JSX.Element | null>(null);
    const [moduleName, setModuleName] = useState('');
    const [companySubdomain, setCompanySubdomain] = useState('');
    const [photoTemp, setPhotoTemp] = useState<string | undefined>(undefined);
    const [menuHidden, setMenuHidden] = useState(false);
    const [disabledModules, setDisabledModules] = useState<string[]>([]);
    const [networkType, setNetworkType] = useState<string>('');
    const [unreadMessages, setUnreadMessages] = useState<number>(0);
    const [title, setTitle] = useState<string>('');
    const [diagnosticAlerts, setDiagnosticAlerts] = useState<boolean>(false);
    const [deviceModel, setDeviceModel] = useState<string>('');
    const [deviceId, setDeviceId] = useState<string>('');
    const [versionWithBuild, setVersionWithBuild] = useState<string>('');
    const [platform, setPlatform] = useState<string>('');
    const [fcmToken, setFcmToken] = useState<string>('');

    const { authorizedMobileEmployeeApi } = useMobileApi(EmployeeMobileVovClient);
    const navigate = useNavigate();
    const params = useParams();

    // window.onAppMount = () => {
    //     if (diagnosticAlerts)
    //         enqueueSnackbar('App foregrounded', { variant: 'info' });

    //     if (loggedIn)
    //         window.location.reload();
    // }

    window.sendNetworkType = (networkType: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('Network: ' + networkType, { variant: 'info' });
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) window.sendNetworkType' + networkType,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setNetworkType(networkType);
    };

    window.sendPlatform = (platform: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('Platform: ' + platform, { variant: 'info' });
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) window.sendPlatform' + platform,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setPlatform(platform);
    };

    window.sendAppVersion = (version: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('App version: ' + version, { variant: 'info' });
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) window.sendAppVersion' + version,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setVersionWithBuild(version);
    }

    window.sendDeviceInfo = (model: string, id: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('Device: ' + model + ' ' + id, { variant: 'info' });
        setDeviceModel(model);
        setDeviceId(id);
    }

    window.refreshNotifications = () => {
        if (diagnosticAlerts)
            enqueueSnackbar('Refresh notifications', { variant: 'info' });
        requestsApi.getUnreadMessagesCount().then((res) => {
            setUnreadMessages((res.newUnreadMessages ?? 0) + (res.activeUnreadMessages ?? 0) + (res.resolvedUnreadMessages ?? 0));
            localStorage.setItem('unread_messages', (res.newUnreadMessages ?? 0) + (res.activeUnreadMessages ?? 0) + (res.resolvedUnreadMessages ?? 0) + '');
        });
    };

    window.sendFcmToken = (token: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('(Context) FCM Token: ' + token, { variant: 'info' });
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) window.sendFcmToken' + token,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setFcmToken(token);
    };

    const handleInitialLoad = useCallback(() => {
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) handleInitialLoad call',
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));

        const mobileBearer = localStorage.getItem('mobile_bearer');
        const mobileDomain = localStorage.getItem('mobile_domain');
        const diagnosticAlerts = localStorage.getItem('diagnostic_alerts');
        console.log("Initial load", diagnosticAlerts);
        setDiagnosticAlerts(diagnosticAlerts === 'true');

        if (mobileBearer && mobileDomain) {
            requestsApi.getUnreadMessagesCount().then((res) => {
                setUnreadMessages((res.newUnreadMessages ?? 0) + (res.activeUnreadMessages ?? 0) + (res.resolvedUnreadMessages ?? 0));
                localStorage.setItem('unread_messages', (res.newUnreadMessages ?? 0) + (res.activeUnreadMessages ?? 0) + (res.resolvedUnreadMessages ?? 0) + '');
            });
            setCompanySubdomain(mobileDomain);
            client.authorizedMobileEmployeeApi.getDisabledModules().then((response) => {
                if (response.disabledModules)
                    setDisabledModules(response.disabledModules);
            });
            employeeApi.employeeMe().then((res) => {
                if (res) {
                    if (res.adminAccessToken)
                        localStorage.setItem('old_bearer', res.adminAccessToken);
                    if (res.adminRefreshToken)
                        localStorage.setItem('old_refresh_token', res.adminRefreshToken);
                    setEmployeeInfo(res);

                    setLoggedIn(true);
                }
            }).catch(() => {
                setLoggedIn(false);
            });
        }
        else {
            setLoggedIn(false);
        }
    }, [client.authorizedMobileEmployeeApi, deviceId, deviceModel, employeeApi, recordsApi, requestsApi, versionWithBuild]);

    useEffect(() => {

        const fetchUnreadDocuments = async () => {
            await authorizedMobileEmployeeApi.getHasUnreadBozpDocumentsNewsMobile().then((response) => {
                if (response.unread)
                    navigate(getFirstPartPath(params.lang ?? 'cs') + 'employee/documents');
            });
        };
        if (loggedIn) {
            fetchUnreadDocuments();
        }

    }, [loggedIn]);

    const setLoggedInCallback = useCallback((data: AuthMobileEmployeeResponse) => {
        setLoggedIn(true);
        localStorage.setItem('mobile_bearer', data.mobileToken!);
        localStorage.setItem('employee_bearer', data.employeeToken!);
        localStorage.setItem('employee_refresh_token', data.employeeRefreshToken!);
        if (data.adminAccessToken)
            localStorage.setItem('old_bearer', data.adminAccessToken);
        if (data.adminRefreshToken)
            localStorage.setItem('old_refresh_token', data.adminRefreshToken);
        setCompanySubdomain(localStorage.getItem('mobile_domain') ?? '');
        handleInitialLoad();
    }, [handleInitialLoad]);
    const logout = useCallback(async () => {
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) Logout',
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));

        //@ts-ignore
        if (window.ReactNativeWebView)
            await employeeApi.deleteCurrentFcmToken(new DeleteCurrentFcmTokenRequest({ fcmToken: fcmToken }));

        logoutEmployee();
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(EmployeeLayoutProvider) Logout - tokens removed',
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setLoggedIn(false);
        setEmployeeInfo(null);
    }, [deviceId, deviceModel, employeeApi, fcmToken, recordsApi, versionWithBuild]);
    useEffect(() => {
        handleInitialLoad();
    }, [handleInitialLoad]);
    const getFirstPartPath = useCallback((lang: string) => {
        return '/' + lang + '/' + companySubdomain + '/'
    }, [companySubdomain]);
    return (
        <EmployeeLayoutContext.Provider value={{ employeeInfo, loggedIn, login: setLoggedInCallback, logout: logout, companySubdomain, getFirstPartPath, disabledModules, moduleButton, setModuleButton, moduleName, setModuleName, photoTemp, setPhotoTemp, menuHidden, setMenuHidden, moduleSecondButton, setModuleSecondButton, networkType, unreadMessages, setUnreadMessages, title, setTitle, diagnosticAlerts, setDiagnosticAlerts, deviceModel, setDeviceModel, deviceId, setDeviceId, platform, setPlatform, fcmToken, setFcmToken, versionWithBuild, setVersionWithBuild }}>
            {children}
        </EmployeeLayoutContext.Provider>
    );
}
export const useEmployeeContext = () => {
    const context = useContext(EmployeeLayoutContext);
    if (context === undefined) {
        throw new Error('useUserLayoutContext must be used within a UserLayoutProvider');
    }
    return context;
};